import { useQueryClient } from "@tanstack/react-query";
import { addWarning } from "@wayflyer/blackbox-fe";
import { AnalyticsScreenProvider, useMediaQuery, OnboardingLayout } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";
import { useSpaNavigate, WayflyerError } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { AboutYourBusiness } from "../../components";
import { MESSAGES } from "../../components/about-your-business/message-definitions";
import { CONNECT_APP_ROUTE_PATHS } from "../../components/constants";
import { composePath, PAGE_TYPE } from "../../hooks/navigation-next/use-funding-application-navigation-next";
import { getFundingApplicationStagesQueryOptions } from "../../queries/funding-application-configuration/queries";
import {
  isRejectedFundingApplicationStateTransition,
  useFundingApplicationMutation,
  useFundingApplicationStateMutation,
} from "../../queries/funding-application/queries";

const FORM_ID = "about-your-business-form";

export interface AboutYourBusinessPageProps {
  onBack: () => void;
}
export const AboutYourBusinessPage = ({ onBack }: AboutYourBusinessPageProps) => {
  const intl = useIntl();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();
  const setApplicationState = useFundingApplicationStateMutation(customerDomain);
  const { isPending: isFormSubmitting } = useFundingApplicationMutation(customerDomain);
  const isSubmitting = isFormSubmitting || setApplicationState.isPending;
  const { navigate } = useSpaNavigate("FundingApp");

  const setStateAndContinue = async () => {
    try {
      await setApplicationState.mutateAsync({
        applicationState: "in_progress",
      });
    } catch (error) {
      if (isRejectedFundingApplicationStateTransition(error)) {
        // Hook's onError has already redirected to root — stop here.
        return;
      }
      throw error;
    }

    try {
      const stages = await queryClient.fetchQuery({
        ...getFundingApplicationStagesQueryOptions(customerDomain),
        retry: 1,
      });
      if (stages.data === null) {
        // If stages are null then some error has occurred and we may not have populated the configuration correctly
        throw new WayflyerError(
          "Failed to fetch populated funding application stages configuration after marking application as in progress",
          "500"
        );
      }
      navigate(composePath(PAGE_TYPE.BANKING));
    } catch (error) {
      addWarning("Failed to fetch funding application stages configuration", {
        customerDomain,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };

  const handleContinue = async (result: FundingApplicationSchemas.CustomerFundingApplicationScreen) => {
    if (result.applicationState === "ineligible") {
      navigate(CONNECT_APP_ROUTE_PATHS.INELIGIBLE);
      return;
    }
    await setStateAndContinue();
  };

  return (
    <AnalyticsScreenProvider screen="onboarding-about-your-business">
      <OnboardingLayout
        variant="wide"
        formId={FORM_ID}
        primaryActionType="submit"
        title={intl.formatMessage(MESSAGES.aboutYourBusinessHeading)}
        subtitle={intl.formatMessage(MESSAGES.aboutYourBusinessSubheading)}
        actions={{
          primary: {
            content: intl.formatMessage(MESSAGES.bankAccountLabel),
            analyticsId: "about-your-business-next",
            icon: IconArrowRight24Line,
            loading: isSubmitting,
            disabled: isSubmitting,
          },
          secondary: {
            content: isMobile ? undefined : intl.formatMessage(MESSAGES.backButtonLabel),
            icon: IconArrowLeft24Line,
            analyticsId: "about-your-business-back",
            onAction: onBack,
          },
        }}
      >
        <AboutYourBusiness formId={FORM_ID} onContinue={handleContinue} />
      </OnboardingLayout>
    </AnalyticsScreenProvider>
  );
};
